/* eslint-disable no-console */

import { isLogged } from '@lib/config';
import { LogLevel } from '@lib/types';

const LOG_LEVEL: LogLevel = (process.env.LOG_LEVEL as LogLevel) || 'info';

const colors = {
  reset: '\x1b[0m',
  yellow: '\x1b[33m',
  green: '\x1b[32m',
  red: '\x1b[31m',
  blue: '\x1b[34m',
  blueBright: '\x1b[94m'
};

const colorize = (color: keyof typeof colors, str: string) =>
  `${colors[color]}${str}${colors.reset}`;

const getPrefix = (logLevel: LogLevel, prefix?: string) => {
  let level = `[${new Date().toISOString()}] [${logLevel.toUpperCase()}]`;

  switch (logLevel) {
    case 'warn':
      level = colorize('yellow', level);
      break;
    case 'log':
      level = colorize('green', level);
      break;
    case 'error':
      level = colorize('red', level);
      break;
    case 'debug':
      level = colorize('blue', level);
      break;
    default:
      break;
  }

  if (prefix) {
    level += ` ${colorize('blueBright', `[${prefix}]`)}`;
  }

  return level;
};

const getLogger = (prefix?: string, logLevel?: LogLevel) => {
  const level = logLevel || LOG_LEVEL;

  return {
    level,
    prefix,
    debug: (...args: any[]) => {
      if (!isLogged('debug', level)) return;

      if (args[0] && typeof args[0] === 'string') {
        args[0] = `${getPrefix('debug', prefix)} ${args[0]}`;
      }

      console.debug(...args);
    },
    log: (...args: any[]) => {
      if (!isLogged('log', level)) return;

      if (args[0] && typeof args[0] === 'string') {
        args[0] = `${getPrefix('log', prefix)} ${args[0]}`;
      }

      console.info(...args);
    },
    warn: (...args: any[]) => {
      if (!isLogged('warn', level)) return;

      if (args[0] && typeof args[0] === 'string') {
        args[0] = `${getPrefix('warn', prefix)} ${args[0]}`;
      }

      console.warn(...args);
    },

    error: (...args: any[]) => {
      if (!isLogged('error', level)) return;

      if (args[0] && typeof args[0] === 'string') {
        args[0] = `${(getPrefix('error'), prefix)} ${args[0]}`;
      }

      console.error(...args);
    }
  };
};

type Logger = ReturnType<typeof getLogger>;

const log = getLogger();

export { log, getLogger };
export type { Logger };
